import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { authConfig, loginRequest } from "../authConfig";
import { CookiesUtil } from "./CookiesUtil";

let msalInstance: PublicClientApplication | null = null;
let tokenPromise: Promise<any> | null = null; // Singleton promise to deduplicate refresh requests

const getMsalInstance = () => {
    if (!msalInstance) {
        msalInstance = new PublicClientApplication(authConfig);
    }
    return msalInstance;
};

// Singleton function to acquire token
// If multiple calls happen in parallel, they will all wait for the SAME promise
const getAccessToken = async (account: any) => {
    // If a refresh is already in progress, return the existing promise
    if (tokenPromise) {
        return tokenPromise;
    }

    const msal = getMsalInstance();

    tokenPromise = msal.acquireTokenSilent({
        ...loginRequest,
        account: account
    }).finally(() => {
        // Once the request completes (success or fail), clear the promise
        // so future requests (e.g. 1 hour later) can start a new one.
        tokenPromise = null;
    });

    return tokenPromise;
};

export const fetchUrl = async (
    url: string, method: string,
    extraOptions: {} | null | FormData = null,
    contentType: HeadersInit | undefined = undefined, isThirdPartyAPI: boolean = false
): Promise<Response> => {

    const msal = getMsalInstance();
    const account = msal.getAllAccounts()[0];
    let accessToken = CookiesUtil.getCookieValue("accessToken");

    if (account) {
        try {
            // Use the singleton helper instead of calling acquireTokenSilent directly
            const response = await getAccessToken(account);

            accessToken = response.accessToken;
            // Update cookie to keep it in sync
            CookiesUtil.setCookie("accessToken", response.accessToken, 1);
        } catch (e) {
            console.warn("Silent token acquisition failed", e);
            if (e instanceof InteractionRequiredAuthError) {
                try {
                    // Start interactive flow if silent fails
                    const response = await msal.acquireTokenPopup({
                        ...loginRequest,
                        account: account
                    });
                    accessToken = response.accessToken;
                    CookiesUtil.setCookie("accessToken", response.accessToken, 1);
                } catch (interactiveError) {
                    console.error("Interactive authentication failed", interactiveError);
                    // If interaction also fails (e.g. user closes popup), return 401 to trigger logout logic
                    return new Response(null, { status: 401, statusText: "Interaction Required" });
                }
            }
            // Fallback to existing cookie value if silent auth fails
        }
    }

    const headers = new Headers(contentType);
    const bearer = `Bearer ${accessToken}`;
    if (!isThirdPartyAPI) {
        headers.append("Authorization", bearer);
    }
    const options = {
        method: method,
        credentials: "include" as RequestCredentials,
        headers: headers
    };

    Object.assign(options, extraOptions);

    if (url.startsWith("http")) {
        return fetch(url, options)
    } else {
        return fetch(process.env.REACT_APP_API_URL + url, options)
    }
}

export function handleLogoutOnSessionExpire(response: any) {
    const pca = new PublicClientApplication(authConfig);
    if (response.status === 401) {
        pca.logoutRedirect().catch((e: any) => {
            console.error(e);
        });
    }
}